import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ActionDecision, AssistantAction } from '../types';
import { useAssistant } from '../AssistantProvider';
import { CheckIcon, CloseIcon } from '../icons';

/*
 * Action-confirmation card. The agent never executes mutations — this card is
 * the commit point. Confirm (solid navy) / Reject (outline, hover red). After a
 * decision the buttons collapse to a status line. Preview fields render in the
 * documented per-kind order; unknown kinds fall back to the payload's own order.
 */

const PREVIEW_ORDER: Record<string, string[]> = {
  CREATE_SUPPORT_REQUEST: ['request_type', 'subject', 'message'],
  TRIGGER_COMPLIANCE_CHECK: ['company_name', 'screen_ubos', 'screen_directors'],
  REGISTER_COMPANY: [
    'primary_name',
    'country',
    'duns_number',
    'registration_number',
  ],
  TRIGGER_BATCH_COMPLIANCE_CHECK: [
    'company_count',
    'portfolio_external_id',
    'screen_ubos',
    'screen_directors',
  ],
  CREATE_PORTFOLIO: ['name'],
  ADD_COMPANY_TO_PORTFOLIO: [
    'portfolio',
    'primary_name',
    'duns_number',
    'country',
  ],
  CREATE_BINDER: ['name'],
  ADD_ITEM_TO_BINDER: ['binder', 'article_external_id'],
  REQUEST_COMPANY_INVESTIGATION: [
    'legal_name',
    'trade_name',
    'country',
    'website_url',
    'address_street',
    'address_postal',
    'address_place',
    'address_phone',
    'officer_name',
    'description',
  ],
};

const humanize = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bId\b/, 'ID')
    .replace(/Duns/i, 'DUNS');

// Preview values are usually flat strings/numbers/booleans, but some action
// kinds (e.g. RATE_COMPLIANCE_RESULTS "items") carry arrays of objects.
// String(v) on those yields "[object Object]"; recurse into arrays/objects
// instead so every leaf renders as a readable primitive. Nested object keys
// that look like ids (e.g. result_external_id) are dropped - they're never
// meaningful to a user and the id itself still shows up via other rows.
// `translateBool` defaults to English so the pure formatter is testable
// without i18next; the component calls it with `t`.
export const fmt = (
  v: unknown,
  translateBool: (s: 'Yes' | 'No') => string = (s) => s
): string => {
  if (v == null) return '';
  if (typeof v === 'boolean') return translateBool(v ? 'Yes' : 'No');
  if (Array.isArray(v)) {
    return v
      .map((x) => fmt(x, translateBool))
      .filter((s) => s !== '')
      .join(', ');
  }
  if (typeof v === 'object') {
    const entries = Object.entries(v as Record<string, unknown>).filter(
      ([k]) => !/_id$/i.test(k)
    );
    return (entries.length ? entries : Object.entries(v))
      .map(([, val]) => fmt(val, translateBool))
      .filter((s) => s !== '')
      .join(' ');
  }
  return String(v);
};

export const buildRows = (
  kind: string,
  preview: Record<string, unknown>,
  fieldLabels: Record<string, string>,
  translateBool: (s: 'Yes' | 'No') => string = (s) => s
): (readonly [string, string])[] => {
  const preferredKeys = PREVIEW_ORDER[kind] || [];
  const orderedKeys = [
    ...preferredKeys,
    ...Object.keys(preview).filter((key) => !preferredKeys.includes(key)),
  ];
  return orderedKeys
    .filter((k) => preview[k] != null && preview[k] !== '')
    .map(
      (k) =>
        [fieldLabels[k] ?? humanize(k), fmt(preview[k], translateBool)] as const
    );
};

const ActionCard = ({ action }: { action: AssistantAction }) => {
  const { t } = useTranslation('assistant');
  const { decideAction } = useAssistant();
  const [deciding, setDeciding] = useState(false);

  // If decideAction's request fails, the provider reverts action.decision to
  // null — re-enable the buttons so the user can retry.
  useEffect(() => {
    if (!action.decision) setDeciding(false);
  }, [action.decision]);

  // Static t() literals for the known preview fields so i18next-parser extracts
  // and translates them; unknown keys fall back to a humanized key.
  const fieldLabels: Record<string, string> = {
    request_type: t('Type'),
    subject: t('Subject'),
    message: t('Message'),
    company_name: t('Company'),
    primary_name: t('Name'),
    country: t('Country'),
    duns_number: t('DUNS'),
    registration_number: t('Registration number'),
    company_count: t('Companies'),
    portfolio_external_id: t('Portfolio'),
    portfolio: t('Portfolio'),
    screen_ubos: t('Screen UBOs'),
    screen_directors: t('Screen directors'),
    name: t('Name'),
    binder: t('Binder'),
    article_external_id: t('Article'),
    legal_name: t('Legal name'),
    trade_name: t('Trade name'),
    website_url: t('Website'),
    address_street: t('Street'),
    address_postal: t('Postal code'),
    address_place: t('City'),
    address_phone: t('Phone'),
    officer_name: t('Officer'),
    description: t('Description'),
  };

  const preview = action.preview || {};
  const rows = buildRows(action.kind, preview, fieldLabels, t);

  const decide = (decision: 'confirm' | 'reject') => {
    if (deciding) return;
    setDeciding(true);
    decideAction(action, decision);
  };

  const actionLabel = action.title || humanize(action.kind);
  const decidedText: Record<ActionDecision, string> = {
    confirm: t('Confirmed: {{action}}', { action: actionLabel }),
    reject: t('Rejected: {{action}}', { action: actionLabel }),
    expired: t('Expired: {{action}}', { action: actionLabel }),
  };

  return (
    <div className="bra-card">
      <div className="bra-ct">
        <b>{actionLabel}</b>
        <span className="bra-ktag">{action.kind}</span>
      </div>
      <div className="bra-prev">
        {rows.map(([k, v]) => (
          <div className="bra-prow" key={k}>
            <span className="bra-k">{k}</span>
            <span className="bra-v">{v}</span>
          </div>
        ))}
      </div>

      {action.decision === 'confirm' ? (
        <div className="bra-decided bra-ok">
          <CheckIcon className="bra-ok" width={13} height={13} />
          {decidedText.confirm}
        </div>
      ) : action.decision === 'reject' || action.decision === 'expired' ? (
        <div className="bra-decided bra-no">
          <CloseIcon width={13} height={13} />
          {decidedText[action.decision]}
        </div>
      ) : (
        <>
          <div className="bra-cbtns">
            <button
              type="button"
              className="bra-btn bra-pri"
              disabled={deciding}
              onClick={() => decide('confirm')}
            >
              {t('Confirm')}
            </button>
            <button
              type="button"
              className="bra-btn bra-sec"
              disabled={deciding}
              onClick={() => decide('reject')}
            >
              {t('Reject')}
            </button>
          </div>
          {action.note ? <div className="bra-cnote">{action.note}</div> : null}
        </>
      )}
    </div>
  );
};

export default ActionCard;
